import { useState, ChangeEvent, FormEvent } from 'react';
import axios from 'axios';
import { PageHero } from '../../components/PageHero';
import SEO from '../../components/SEO';
import { BASE_URL } from '../../services/api';



interface ContactForm {
  first_name: string;
  last_name: string;
  email: string;
  organisation: string;
  inquirer_type: string;
  enquiry_type: string;
  message: string;
}

const initialForm: ContactForm = {
  first_name: '',
  last_name: '',
  email: '',
  organisation: '',
  inquirer_type: '',
  enquiry_type: 'general',
  message: '',
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validateForm(form: ContactForm): Partial<Record<keyof ContactForm, string>> {
  const errors: Partial<Record<keyof ContactForm, string>> = {};

  if (!form.first_name.trim()) errors.first_name = 'First name is required';
  if (!form.last_name.trim()) errors.last_name = 'Last name is required';

  if (!form.email.trim()) {
    errors.email = 'Email is required';
  } else if (!EMAIL_REGEX.test(form.email.trim())) {
    errors.email = 'Enter a valid email address';
  }

  if (!form.inquirer_type) {
    errors.inquirer_type = 'Please select an enquiry type';
  }

  if (!form.message.trim()) {
    errors.message = 'Message is required';
  } else if (form.message.trim().length < 10) {
    errors.message = 'Message should be at least 10 characters';
  }

  return errors;
}

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function Contact() {
  const [form, setForm] = useState<ContactForm>(initialForm);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof ContactForm, string>>>({});
  const [status, setStatus] = useState<Status>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (fieldErrors[name as keyof ContactForm]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[name as keyof ContactForm];
        return next;
      });
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const errors = validateForm(form);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setStatus('idle');
      return;
    }

    setStatus('submitting');
    setErrorMsg('');

    try {
      await axios.post(
        `${BASE_URL}/contact/enquiry/`,
        {
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          email: form.email.trim(),
          organisation: form.organisation.trim(),
          inquirer_type: form.inquirer_type,
          enquiry_type: form.enquiry_type,
          message: form.message.trim(),
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      setStatus('success');
      setForm(initialForm);
      setFieldErrors({});
    } catch (err) {
      setStatus('error');
      if (axios.isAxiosError(err)) {
        setErrorMsg(err.response?.data?.message || err.message || 'Something went wrong. Please try again.');
      } else {
        setErrorMsg('Something went wrong. Please try again.');
      }
    }
  };

  const inputClass = (field: keyof ContactForm) =>
    `w-full border rounded-lg px-3 py-2.5 text-[12px] text-gray-900 focus:outline-none ${fieldErrors[field]
      ? 'border-red-400 focus:border-red-500'
      : 'border-gray-200 focus:border-blue-400'
    }`;

  return (
    <div>
      <SEO
        title="Contact MannaPlag: Demos, Institutional Plans & Support"
        description="Contact our team to schedule an institutional product demo, inquire about publisher API integrations, or receive dedicated technical customer support."
        canonicalPath="/contact"
        keywords="contact MannaPlag, institutional plagiarism demo, journal api inquiry, plagiarism detection sales, technical support"
      />
      <PageHero
        eyebrow="Contact"
        title="Get in touch"
        subtitle="Whether you have a question about pricing, want to arrange a demo, or need support, we're here to help."
      />

      <section className="bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">

            {/* Form */}
            <div className="bg-white border border-gray-100 rounded-lg p-8">
              <h2 className="text-[15px] font-medium text-gray-900 mb-6">Send us a message</h2>
              <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[11px] font-medium text-gray-700 block mb-1.5">First name</label>
                    <input
                      type="text"
                      name="first_name"
                      value={form.first_name}
                      onChange={handleChange}
                      className={inputClass('first_name')}
                      placeholder="John"
                    />
                    {fieldErrors.first_name && (
                      <p className="text-[11px] text-red-500 mt-1">{fieldErrors.first_name}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-gray-700 block mb-1.5">Last name</label>
                    <input
                      type="text"
                      name="last_name"
                      value={form.last_name}
                      onChange={handleChange}
                      className={inputClass('last_name')}
                      placeholder="Smith"
                    />
                    {fieldErrors.last_name && (
                      <p className="text-[11px] text-red-500 mt-1">{fieldErrors.last_name}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="text-[11px] font-medium text-gray-700 block mb-1.5">Email address</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    className={inputClass('email')}
                    placeholder="john@university.edu"
                  />
                  {fieldErrors.email && (
                    <p className="text-[11px] text-red-500 mt-1">{fieldErrors.email}</p>
                  )}
                </div>

                <div>
                  <label className="text-[11px] font-medium text-gray-700 block mb-1.5">Organisation</label>
                  <input
                    type="text"
                    name="organisation"
                    value={form.organisation}
                    onChange={handleChange}
                    className={inputClass('organisation')}
                    placeholder="University or company name"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-medium text-gray-700 block mb-1.5">Enquiry type</label>
                  <select
                    name="inquirer_type"
                    value={form.inquirer_type}
                    onChange={handleChange}
                    className={inputClass('inquirer_type')}
                  >
                    <option value="">Select an option</option>
                    <option value="individual">Individual plan</option>
                    <option value="institution">Institutional plan</option>
                    <option value="publisher">Publisher plan</option>
                    <option value="support">Technical support</option>
                    <option value="other">Other</option>
                  </select>
                  {fieldErrors.inquirer_type && (
                    <p className="text-[11px] text-red-500 mt-1">{fieldErrors.inquirer_type}</p>
                  )}
                </div>

                <div>
                  <label className="text-[11px] font-medium text-gray-700 block mb-1.5">Message</label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    rows={4}
                    className={`${inputClass('message')} resize-none`}
                    placeholder="Tell us how we can help..."
                  />
                  {fieldErrors.message && (
                    <p className="text-[11px] text-red-500 mt-1">{fieldErrors.message}</p>
                  )}
                </div>

                {status === 'success' && (
                  <p className="text-[12px] text-green-600">Thanks — your message has been sent!</p>
                )}
                {status === 'error' && (
                  <p className="text-[12px] text-red-600">{errorMsg}</p>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full py-3 bg-navy text-white text-[12px] font-medium rounded-lg hover:bg-navy-dark transition-colors disabled:opacity-60"
                >
                  {status === 'submitting' ? 'Sending...' : 'Send message'}
                </button>
              </form>
            </div>

            {/* Details */}
            <div className="flex flex-col gap-4">
              {[
                {
                  icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z" />,
                  title: 'Registered office',
                  body: <>Manna Analytics Private Limited<br />3/343, Town Railway Nagar<br />Nagercoil 629001<br />Tamil Nadu, India</>,
                },
                {
                  icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />,
                  title: 'Email',
                  body: <><p className="text-[12px] text-gray-700"><a href={`https://mail.google.com/mail/?view=cm&fs=1&to=support@mannanalytics.com&su=${encodeURIComponent('support')}&body=${encodeURIComponent('helo team ,\n\n')}`} target="_blank" rel="noopener noreferrer" className="hover:underline">support@mannanalytics.com</a></p><p className="text-[11px] text-gray-500 mt-1">We respond within 1 business day</p></>,
                },
                {
                  icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />,
                  title: 'Support hours',
                  body: <><p className="text-[12px] text-gray-700">Monday to Friday, 9am to 6pm IST</p><p className="text-[11px] text-gray-500 mt-1">Institutional customers get extended support</p></>,
                },
              ].map((item, i) => (
                <div key={i} className="bg-white border border-gray-100 rounded-xl p-5 flex gap-4 items-start">
                  <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-navy" fill="none" viewBox="0 0 24 24" stroke="currentColor">{item.icon}</svg>
                  </div>
                  <div>
                    <p className="text-[12px] font-medium text-gray-900 mb-1">{item.title}</p>
                    <div className="text-[12px] text-gray-500 leading-relaxed">{item.body}</div>
                  </div>
                </div>
              ))}

              <div className="bg-navy rounded-xl p-6">
                <p className="text-[13px] font-medium text-white mb-2">Looking for an institutional or enterprise plan?</p>
                <p className="text-[12px] text-blue-200 leading-relaxed mb-4">We offer dedicated demos for universities and publishers. Let us show you how Manna Plag fits your workflow.</p>
                <a href={`https://mail.google.com/mail/?view=cm&fs=1&to=support@mannaanalytics.com&su=${encodeURIComponent('Sales Enquiry - Institutional/Enterprise Plan')}&body=${encodeURIComponent('Hi Manna Plag Sales Team,\n\n')}`} target="_blank" rel="noopener noreferrer" className="text-[12px] text-white underline">
                  Email our sales team
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}